import { useQuery } from "@tanstack/react-query";
import { logger } from "@/utils/logger";
import { useApi } from "@/hooks/useApi"; // authenticated instance — NOT the dev-only `api` singleton
import type {
  FeatureParamSpec,
  FeatureTypeSpec,
  SlotDefault,
} from "@/types/api";

// Re-export the catalog interfaces from their canonical home (@/types/api) so existing
// import sites (FeatureBuilder, tests) keep importing them from this hook.
export type { FeatureParamSpec, FeatureTypeSpec, SlotDefault };

export interface FeatureSchema {
  featureTypes: Record<string, FeatureTypeSpec>;
  slotDefaults: SlotDefault[];
  status: "loading" | "loaded" | "fallback";
}

// Built-in fallback — MUST mirror backend build_feature_param_catalog()/DEFAULT_FEATURE_SPECS.
// f5 = RSI period 9 (the slot default), NOT the RSI module default 14; ADX = period only;
// CCI period max 300; f2 (WT) multiplier 11.
export const FALLBACK_TYPES: Record<string, FeatureTypeSpec> = {
  RSI: {
    module_id: "rsi",
    legacy_feature_type: "RSI",
    name: "RSI",
    params: [
      {
        name: "period",
        label: "Period",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 14,
        wire_role: "period",
      },
      {
        name: "smoothing",
        label: "Smoothing",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 2,
        wire_role: "multiplier",
      },
    ],
  },
  WT: {
    module_id: "wavetrend",
    legacy_feature_type: "WT",
    name: "WaveTrend",
    params: [
      {
        name: "channel_length",
        label: "Channel Length",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 10,
        wire_role: "period",
      },
      {
        name: "average_length",
        label: "Average Length",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 11,
        wire_role: "multiplier",
      },
    ],
  },
  CCI: {
    module_id: "cci",
    legacy_feature_type: "CCI",
    name: "CCI",
    params: [
      {
        name: "period",
        label: "Period",
        type: "int",
        min: 1,
        max: 300,
        step: 1,
        default: 20,
        wire_role: "period",
      },
      {
        name: "smoothing",
        label: "Smoothing",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 2,
        wire_role: "multiplier",
      },
    ],
  },
  ADX: {
    module_id: "adx",
    legacy_feature_type: "ADX",
    name: "ADX",
    params: [
      {
        name: "period",
        label: "Period",
        type: "int",
        min: 1,
        max: 200,
        step: 1,
        default: 20,
        wire_role: "period",
      },
    ],
  },
};

export const FALLBACK_SLOT_DEFAULTS: SlotDefault[] = [
  { slot: "f1", type: "RSI", period: 14, multiplier: 2 },
  { slot: "f2", type: "WT", period: 10, multiplier: 11 },
  { slot: "f3", type: "CCI", period: 20, multiplier: 2 },
  { slot: "f4", type: "ADX", period: 20, multiplier: 2 },
  { slot: "f5", type: "RSI", period: 9, multiplier: 2 },
];

export function useFeatureSchema(): FeatureSchema {
  const api = useApi(); // resolves base URL + auth at call time
  const { data, status } = useQuery({
    queryKey: ["feature-schema"],
    queryFn: () => api.getFeatureSchema(),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  if (status === "error") {
    logger.warn("feature-schema unavailable; using built-in fallback catalog");
    return {
      featureTypes: FALLBACK_TYPES,
      slotDefaults: FALLBACK_SLOT_DEFAULTS,
      status: "fallback",
    };
  }
  if (status === "pending" || !data) {
    return {
      featureTypes: FALLBACK_TYPES,
      slotDefaults: FALLBACK_SLOT_DEFAULTS,
      status: "loading",
    };
  }
  return {
    featureTypes: data.feature_types,
    slotDefaults: data.slot_defaults,
    status: "loaded",
  };
}
